/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */
import { type PropsWithChildren } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { messages } from './messages';

const styles = cssMap({
	tableWrapper: {
		width: '100%',
		// do not shrink if the projects table is large, just let the projects table extend past the bottom of the page
		flexShrink: 0,
		overflowY: 'auto',
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),

		'-ms-overflow-style': 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&::-webkit-scrollbar': {
			display: 'none',
		},
	},
	tableWrapperFullHeight: {
		// issues table should expand to take up the container (minus the bottom padding) when the projects table has no results
		maxHeight: `calc(100% - ${token('space.300')})`,
	},
	tableWrapperRestrictedHeight: {
		// issues table should only take up a max of 75% of the container height when the projects table is also shown
		maxHeight: '75%',
	},
	table: {
		display: 'table',
		// table will respect the fixed width of the first column and make the other columns responsive
		tableLayout: 'fixed',
		width: '100%',
		height: '100%',
		// allow explicit control of borders within the table
		borderCollapse: 'separate',
		borderSpacing: '0px',
		borderWidth: '0px',
	},
	thead: {
		display: 'table-header-group',
		// header will float while the user scrolls
		position: 'sticky',
		top: 0,
		zIndex: 1,
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
	},
	headerRow: {
		display: 'table-row',
		height: '40px',
		backgroundColor: token('elevation.surface.sunken'),

		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'> th:first-of-type': {
			// there is a top-level style that targets the first and last cell of any table rows
			// that removes padding so we need to override this by using a descendant selector
			paddingLeft: token('space.200'),

			// the left border is provided by the table's outer container
			borderLeftWidth: '0px',
			borderTopLeftRadius: token('radius.small'),
		},
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'> th:last-of-type': {
			// there is a top-level style that targets the first and last cell of any table rows
			// that removes padding so we need to override this by using a descendant selector
			paddingRight: token('space.200'),

			// the right border is provided by the table's outer container
			borderRightWidth: '0px',
			borderTopRightRadius: token('radius.small'),
		},
	},
	thWork: {
		display: 'table-cell',
		verticalAlign: 'middle',
		width: '456px', // from Figma
		color: token('color.text.subtlest'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		textAlign: 'left',
		whiteSpace: 'nowrap',
	},
	thBase: {
		display: 'table-cell',
		verticalAlign: 'middle',
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		overflowX: 'hidden',
		whiteSpace: 'nowrap',
		borderStyle: 'solid',
		borderColor: token('color.border'),
		// every table cell (except the first and last) will have a right and bottom border
		borderRightWidth: token('border.width'),
		borderBottomWidth: token('border.width'),
		borderTopWidth: '0px',
		borderLeftWidth: '0px',
	},
	tbody: {
		display: 'table-row-group',

		// remove the bottom border from all cells in the last row in the
		// table because this border will be provided by the footer
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'& > tr:last-of-type > td': {
			borderBottomWidth: '0px',
		},
	},
	tfoot: {
		display: 'table-footer-group',
		// footer will float while the user scrolls
		position: 'sticky',
		bottom: '-1px',
		zIndex: 1,
	},
	footerRow: {
		display: 'table-row',
		backgroundColor: token('elevation.surface'),

		// there is a top-level style that targets the first and last cell of any table rows
		// that removes padding so we need to override this by using a descendant selector
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'& > td:first-of-type, & > td:last-of-type': {
			paddingRight: token('space.200'),
			paddingLeft: token('space.200'),
		},
	},
	footerCell: {
		// the footer has an inset top border since it is a fixed component so account for the border width in the content box
		height: `calc(40px + ${token('border.width')} * 1)`,
		boxSizing: 'border-box',
		display: 'table-cell',
		verticalAlign: 'middle',
		textAlign: 'right',
		font: token('font.body'),
		color: token('color.text.subtle'),
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'inset',
		borderColor: token('color.border'),
		borderBottomWidth: 0,
	},
});

const TableHeader = () => {
	const { formatMessage } = useIntl();

	return (
		<thead css={styles.thead}>
			<tr css={styles.headerRow}>
				<th css={[styles.thBase, styles.thWork]}>{formatMessage(messages.workColumnHeader)}</th>
				<th css={[styles.thBase]}>{formatMessage(messages.statusColumnHeader)}</th>
				<th css={[styles.thBase]}>{formatMessage(messages.assigneeColumnHeader)}</th>
				<th css={[styles.thBase]}>{formatMessage(messages.labelsColumnHeader)}</th>
				<th css={[styles.thBase]}>{formatMessage(messages.priorityColumnHeader)}</th>
			</tr>
		</thead>
	);
};

type TableFooterProps = {
	/** Number of results currently shown. */
	shown: number;
	/** Total number of results. Renders as "X+" when total exceeds shown. */
	total: number;
};

const TableFooter = ({ shown, total }: TableFooterProps) => {
	// "25 of 50", or "50 of 50+" in the case that we received more results than we can show the user
	const totalLabel = total > shown ? `${total.toLocaleString()}+` : total.toLocaleString();

	return (
		<tfoot css={styles.tfoot}>
			<tr css={styles.footerRow}>
				<td css={[styles.footerCell]} colSpan={5}>
					{shown} of {totalLabel}
				</td>
			</tr>
		</tfoot>
	);
};

type SearchResultsLiteIssueTableProps = PropsWithChildren<{
	/** Number of rows currently rendered. */
	shownCount: number;
	/** Total number of matching results. */
	totalCount: number;
	/** Whether or not the project table is the only table rendering in the results page (no issues table) */
	isOnlyTableShown: boolean;
}>;

export const SearchResultsLiteIssueTable = ({
	children,
	shownCount,
	totalCount,
	isOnlyTableShown,
}: SearchResultsLiteIssueTableProps): JSX.Element => (
	<div
		css={[
			styles.tableWrapper,
			isOnlyTableShown ? styles.tableWrapperFullHeight : styles.tableWrapperRestrictedHeight,
		]}
	>
		<table css={styles.table} data-testid="search-results-lite-issue-table">
			<TableHeader />
			<tbody css={styles.tbody}>{children}</tbody>
			<TableFooter shown={shownCount} total={totalCount} />
		</table>
	</div>
);
